import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Box, Button, Divider, Grid, Typography } from '@mui/material';

import ErrorMessage from '../../sharedModules/layout/ErrorMessage';
import LoadingWrapper from '../../sharedModules/layout/LoadingWrapper';
import InlineSaveField from './InlineSaveField';

import { PhotoProposal } from '../model';

import axios from '../../sharedModules/utils/axios.utils';

interface PhotoProposalImageGridProps {
  allXoiPhotos: any[];
  photoProposal: PhotoProposal;
  fetchPhotoProposal: () => void;
  photosLoading?: boolean;
}

type PhotoSection = {
  stepName: string;
  stepIndex: number;
  photos: any[];
};

const buildSections = (photos: any[]): PhotoSection[] => {
  const sections: PhotoSection[] = [];
  const seen = new Map<number, PhotoSection>();

  for (const photo of photos) {
    const key = photo.step_index ?? 0;
    if (!seen.has(key)) {
      const section: PhotoSection = {
        stepName: photo.step_name || 'Uncategorized',
        stepIndex: key,
        photos: [],
      };
      seen.set(key, section);
      sections.push(section);
    }
    seen.get(key)!.photos.push(photo);
  }

  return sections;
};

const PhotoProposalImageGrid: FC<PhotoProposalImageGridProps> = (props) => {
  const { xoiJobId } = useParams<{ xoiJobId: string }>();

  const { photoProposal, fetchPhotoProposal, allXoiPhotos, photosLoading } = props;

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [selectedPhotoIds, setSelectedPhotoIds] = useState<string[]>([]);

  const handlePhotoClick = (photoId: string) => {
    setSelectedPhotoIds((prev) => (prev.includes(photoId) ? prev.filter((id) => id !== photoId) : [...prev, photoId]));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response: any = await axios.post(`/photo_proposals/photo_proposals`, {
        photo_page_proposal: {
          xoi_job_id: xoiJobId,
          photo_ids: selectedPhotoIds,
        },
      });
      setErrorMessage('');
      fetchPhotoProposal();
      const downloadUrl = response.data.download_url;
      if (downloadUrl) window.open(downloadUrl, '_blank');
    } catch (error: any) {
      setErrorMessage(error?.response?.data?.error || 'Something went wrong generating the proposal');
    } finally {
      setSaving(false);
    }
  };

  const renderPhoto = (photo: any) => {
    const selectionIndex = selectedPhotoIds.indexOf(photo.id);
    const isSelected = selectionIndex !== -1;
    return (
      <div
        key={photo.id}
        onClick={() => handlePhotoClick(photo.id)}
        style={{ cursor: 'pointer', position: 'relative' }}
      >
        {isSelected && (
          <div
            style={{
              position: 'absolute',
              top: 6,
              left: 6,
              width: 26,
              height: 26,
              borderRadius: '50%',
              backgroundColor: '#1976d2',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 13,
              fontWeight: 700,
              zIndex: 1,
              userSelect: 'none',
            }}
          >
            {selectionIndex + 1}
          </div>
        )}
        <img
          src={photo.url}
          alt={`Uploaded by ${photo.uploader}`}
          style={{
            width: '100%',
            height: '250px',
            objectFit: 'contain',
            backgroundColor: '#111',
            borderRadius: '4px',
            border: isSelected ? '3px solid #1976d2' : '3px solid transparent',
            boxSizing: 'border-box',
          }}
        />
        <div onClick={(e) => e.stopPropagation()}>
          <InlineSaveField
            value={photo.description ?? ''}
            onSave={async (val) => {
              await axios.put(`/photo_proposals/xoi_photos/${photo.id}`, { description: val });
              fetchPhotoProposal();
            }}
          />
        </div>
      </div>
    );
  };

  const sections = buildSections(allXoiPhotos ?? []);

  return (
    <Grid container>
      <Grid item xs={12}>
        <ErrorMessage message={errorMessage} />
      </Grid>
      <Grid item xs={12}>
        <LoadingWrapper isLoading={saving}>
          {sections.length > 0 ? (
            <>
              {sections.map((section, idx) => (
                <Box key={section.stepIndex} sx={{ mb: 4 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                    {section.stepName}
                  </Typography>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                    {section.photos.map(renderPhoto)}
                  </div>
                  {idx < sections.length - 1 && <Divider sx={{ mt: 3 }} />}
                </Box>
              ))}
              <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                <Button variant="contained" onClick={handleSave} disabled={selectedPhotoIds.length === 0 || saving}>
                  {`Create Proposal with ${selectedPhotoIds.length} Photo${selectedPhotoIds.length !== 1 ? 's' : ''}`}
                </Button>
                {photoProposal?.download_url && (
                  <Button
                    variant="outlined"
                    component="a"
                    href={photoProposal.download_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View PDF
                  </Button>
                )}
              </Box>
            </>
          ) : (
            <>
              {photosLoading ? (
                <Typography>Loading...</Typography>
              ) : (
                <Typography>No photos for this Xoi Job</Typography>
              )}
            </>
          )}
        </LoadingWrapper>
      </Grid>
    </Grid>
  );
};

export default PhotoProposalImageGrid;
